// src/pages/Login.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Container,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  useTheme,
} from '@mui/material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Login = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const redirect = location.state?.from?.pathname || '/dashboard';

  const { user, loading, loginWithEmailPassword, initialAuthChecked } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState(''); // Kept if you want to show other messages

  // if session exists and initial auth check is complete, redirect
  useEffect(() => {
    if (initialAuthChecked && user) {
      navigate(redirect, { replace: true });
    }
  }, [user, initialAuthChecked, navigate, redirect]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!email || !password) {
        setError('Email and password are required.');
        return;
    }

    const res = await loginWithEmailPassword(email.trim(), password);
    if (res.success && res.data?.user) {
      // Navigate on successful login, AuthContext will also handle user state
      // No specific message needed here as redirection will occur
      // Or, if you prefer, setMessage('Login successful! Redirecting...');
    } else if (res.error) {
      setError(res.error.message || 'Failed to login. Please check your credentials.');
    } else if (!res.success && !res.error) {
      // This case might occur if signUp auto-login is off and email verification is pending
      // For login, it's more likely an unknown error if not covered by res.error
      setError('An unexpected error occurred during login.');
    }
  };

  // Show loading spinner only during the submit operation itself or initial page load
  const pageLoading = loading && !initialAuthChecked; // For initial load
  const formSubmitting = loading && initialAuthChecked; // For submit operation

  if (pageLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: theme.palette.background.default,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
      }}
    >
      <Container maxWidth="xs">
        <Paper sx={{ position: 'relative', p: 4, borderRadius: 2 }} elevation={4}>
          {formSubmitting && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(255,255,255,0.7)',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 2,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          <Typography variant="h4" align="center" gutterBottom>
            Admin Panel Login
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
              {error}
            </Alert>
          )}
          {message && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {message}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              label="Email Address"
              name="email"
              type="email"
              required
              fullWidth
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 2 }} // Adjusted margin
            />
            <TextField
              label="Password"
              name="password"
              type="password"
              required
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={formSubmitting}
              size="large"
              sx={{ py: 1.5 }}
            >
              {formSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Login'}
            </Button>
          </Box>

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2">
              Don’t have an account?{' '}
              <Link to="/register" style={{ color: theme.palette.primary.main }}>
                Register here
              </Link>
            </Typography>
            {/* 
            Optionally, if you want to keep magic link accessible from login page:
            <Typography variant="body2" sx={{ mt: 1 }}>
              Or, <Button onClick={() => navigate('/magic-link-login')}>Login with Magic Link</Button>
            </Typography>
            You would need to create a new route/page for magic link if you choose this.
            For now, this example focuses on email/password for the main login page.
            */}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;